import { hasUserPermission } from '@controlstock/contracts';
import type { AuthSession } from '@controlstock/contracts';
import {
  Banknote,
  CreditCard,
  Bell,
  Boxes,
  ChevronDown,
  LayoutDashboard,
  LockKeyhole,
  LogOut,
  Menu,
  Settings2,
  ShoppingBag,
  ReceiptText,
  Truck,
  FlaskConical,
  Factory,
  UserRoundCheck,
  WalletCards,
  ShieldCheck,
  UnlockKeyhole,
  UsersRound,
  Wrench,
  BarChart3,
  Landmark,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Brand } from '@/components/brand';
import { OrganizationStateBadge } from '@/components/status-badge';
import { useJobs } from '@/hooks';

type Icon = typeof LayoutDashboard;
type SidebarLockMode = 'auto' | 'open' | 'closed';

type NavigationItem = Readonly<{
  to: string;
  label: string;
  icon: Icon;
  adminOnly?: boolean;
  end?: boolean;
}>;

const operationsNavigation: readonly NavigationItem[] = [
  { to: '/dashboard', label: 'Resumen', icon: LayoutDashboard, adminOnly: true },
  { to: '/inventario', label: 'Inventario', icon: Boxes },
  { to: '/produccion', label: 'Producción', icon: Factory, adminOnly: true, end: true },
  { to: '/produccion/talleres', label: 'Trabajos en talleres', icon: Wrench, adminOnly: true },
  { to: '/produccion/recepciones', label: 'Recepciones', icon: ReceiptText, adminOnly: true },
];

const salesNavigation: readonly NavigationItem[] = [
  { to: '/ventas/pedidos', label: 'Pedidos', icon: ShoppingBag },
  { to: '/ventas/facturacion', label: 'Facturación', icon: ReceiptText },
  { to: '/ventas/cobros', label: 'Cobros', icon: WalletCards },
  { to: '/ventas/deuda-clientes', label: 'Deuda de clientes', icon: WalletCards },
  { to: '/ventas/clientes', label: 'Clientes', icon: UsersRound },
  { to: '/ventas/vendedores', label: 'Vendedores', icon: UserRoundCheck },
];

function canAccessSales(session: AuthSession): boolean {
  return hasUserPermission(session.user, 'sales.read');
}

const purchasesNavigation: readonly NavigationItem[] = [
  { to: '/compras/facturacion', label: 'Facturación', icon: ReceiptText, adminOnly: true },
  { to: '/compras/pagos', label: 'Pagos', icon: CreditCard, adminOnly: true },
  {
    to: '/compras/deuda-proveedores',
    label: 'Deuda a proveedores',
    icon: CreditCard,
    adminOnly: true,
  },
  { to: '/compras/gastos', label: 'Gastos', icon: Banknote, adminOnly: true },
  { to: '/compras/proveedores', label: 'Proveedores', icon: Truck, adminOnly: true },
  { to: '/compras/materias-primas', label: 'Materias primas', icon: FlaskConical, adminOnly: true },
];

const treasuryNavigation: readonly NavigationItem[] = [
  { to: '/tesoreria/bancos', label: 'Bancos', icon: Landmark, adminOnly: true },
  { to: '/tesoreria/cheques', label: 'Cheques', icon: WalletCards, adminOnly: true },
];

const reportsNavigation: readonly NavigationItem[] = [
  { to: '/reportes/ventas', label: 'Ventas', icon: BarChart3 },
];

const settingsNavigation: readonly NavigationItem[] = [
  { to: '/configuracion', label: 'Configuración', icon: Settings2, adminOnly: true },
  { to: '/configuracion/empleados', label: 'Empleados', icon: UsersRound, adminOnly: true },
  {
    to: '/configuracion/procesos-talleres',
    label: 'Procesos de talleres',
    icon: Wrench,
    adminOnly: true,
  },
];

function initials(displayName: string): string {
  const names = displayName.trim().split(/\s+/);
  return names
    .slice(0, 2)
    .map((name) => name.slice(0, 1).toUpperCase())
    .join('');
}

function navigationLink(
  item: NavigationItem,
  compact: boolean,
  onNavigate?: () => void,
): React.JSX.Element {
  const IconComponent = item.icon;
  return (
    <NavLink
      key={item.to}
      to={item.to}
      end={item.end ?? false}
      onClick={onNavigate}
      title={compact ? item.label : undefined}
      aria-label={compact ? item.label : undefined}
      className={({ isActive }) =>
        `group flex items-center rounded-xl py-2.5 text-sm font-medium transition-colors ${
          compact ? 'justify-center px-2' : 'gap-3 px-3'
        } ${
          isActive
            ? 'bg-brand-100 text-ink-950 shadow-[inset_0_0_0_1px_rgb(188_135_52/0.10)]'
            : 'text-slate-600 hover:bg-brand-50 hover:text-ink-950'
        }`
      }
    >
      <IconComponent size={18} strokeWidth={1.9} aria-hidden="true" className="shrink-0" />
      {!compact && <span className="truncate">{item.label}</span>}
    </NavLink>
  );
}

function Sidebar({
  session,
  compact = false,
  onNavigate,
  onLogout,
  lockMode,
  onToggleLock,
}: Readonly<{
  session: AuthSession;
  compact?: boolean;
  onNavigate?: () => void;
  onLogout: () => void;
  lockMode?: SidebarLockMode;
  onToggleLock?: () => void;
}>): React.JSX.Element {
  const visibleOperations = operationsNavigation.filter((item) =>
    item.to === '/dashboard'
      ? hasUserPermission(session.user, 'dashboard.read')
      : item.to === '/inventario'
        ? hasUserPermission(session.user, 'inventory.read')
        : !item.adminOnly || session.user.role === 'ADMIN',
  );
  const visiblePurchases = purchasesNavigation.filter(
    (item) => !item.adminOnly || session.user.role === 'ADMIN',
  );
  const visibleSettings = settingsNavigation.filter(
    (item) => !item.adminOnly || session.user.role === 'ADMIN',
  );
  const visibleSales = canAccessSales(session) ? salesNavigation : [];
  const visibleReports = hasUserPermission(session.user, 'reports.sales.read')
    ? reportsNavigation
    : [];
  const visibleTreasury = treasuryNavigation.filter(
    (item) => !item.adminOnly || session.user.role === 'ADMIN',
  );
  const isLocked = lockMode === 'open' || lockMode === 'closed';

  return (
    <aside
      className={`flex h-full flex-col overflow-hidden border-r border-brand-100/80 bg-[#fffdf9] py-5 text-ink-950 shadow-xl shadow-slate-950/5 transition-[width,padding] duration-200 ease-out ${
        compact ? 'w-20 px-3' : 'w-[272px] px-4'
      }`}
    >
      <div
        className={`flex shrink-0 ${compact ? 'flex-col items-center gap-3' : 'items-center gap-3 px-2'}`}
      >
        <Brand compact={compact} className={compact ? 'justify-center' : ''} />
        {onToggleLock && (
          <button
            type="button"
            onClick={onToggleLock}
            className={`shrink-0 rounded-lg p-2 transition-colors hover:bg-brand-100 hover:text-ink-950 ${
              compact ? 'text-slate-500' : 'ml-auto text-slate-500'
            }`}
            title={
              isLocked
                ? 'Desbloquear sidebar automático'
                : `Bloquear ${compact ? 'cerrado' : 'abierto'}`
            }
            aria-label={
              isLocked
                ? 'Desbloquear sidebar automático'
                : `Bloquear sidebar ${compact ? 'cerrado' : 'abierto'}`
            }
          >
            {isLocked ? (
              <LockKeyhole size={16} aria-hidden="true" />
            ) : (
              <UnlockKeyhole size={16} aria-hidden="true" />
            )}
          </button>
        )}
      </div>
      <div className={`${compact ? 'mt-5' : 'mt-8'} flex-1 overflow-y-auto app-scrollbar`}>
        {!compact && (
          <p className="px-3 pb-2 text-[10px] font-semibold tracking-[0.16em] text-slate-400 uppercase">
            Operación
          </p>
        )}
        <nav className="space-y-1" aria-label="Operación">
          {visibleOperations.map((item) => navigationLink(item, compact, onNavigate))}
        </nav>
        {visibleSales.length > 0 && (
          <>
            {!compact && (
              <p className="mt-7 px-3 pb-2 text-[10px] font-semibold tracking-[0.16em] text-slate-400 uppercase">
                Ventas
              </p>
            )}
            <nav
              className={`${compact ? 'mt-4 border-t border-brand-100 pt-4' : ''} space-y-1`}
              aria-label="Ventas"
            >
              {visibleSales.map((item) => navigationLink(item, compact, onNavigate))}
            </nav>
          </>
        )}
        {visiblePurchases.length > 0 && (
          <>
            {!compact && (
              <p className="mt-7 px-3 pb-2 text-[10px] font-semibold tracking-[0.16em] text-slate-400 uppercase">
                Compras
              </p>
            )}
            <nav
              className={`${compact ? 'mt-4 border-t border-brand-100 pt-4' : ''} space-y-1`}
              aria-label="Compras"
            >
              {visiblePurchases.map((item) => navigationLink(item, compact, onNavigate))}
            </nav>
          </>
        )}
        {visibleReports.length > 0 && (
          <>
            {!compact && (
              <p className="mt-7 px-3 pb-2 text-[10px] font-semibold tracking-[0.16em] text-slate-400 uppercase">
                Reportes
              </p>
            )}
            <nav
              className={`${compact ? 'mt-4 border-t border-brand-100 pt-4' : ''} space-y-1`}
              aria-label="Reportes"
            >
              {visibleReports.map((item) => navigationLink(item, compact, onNavigate))}
            </nav>
          </>
        )}
        {visibleTreasury.length > 0 && (
          <>
            {!compact && (
              <p className="mt-7 px-3 pb-2 text-[10px] font-semibold tracking-[0.16em] text-slate-400 uppercase">
                Tesorería
              </p>
            )}
            <nav
              className={`${compact ? 'mt-4 border-t border-brand-100 pt-4' : ''} space-y-1`}
              aria-label="Tesorería"
            >
              {visibleTreasury.map((item) => navigationLink(item, compact, onNavigate))}
            </nav>
          </>
        )}
        {visibleSettings.length > 0 && (
          <>
            {!compact && (
              <p className="mt-7 px-3 pb-2 text-[10px] font-semibold tracking-[0.16em] text-slate-400 uppercase">
                Sistema
              </p>
            )}
            <nav
              className={`${compact ? 'mt-4 border-t border-brand-100 pt-4' : ''} space-y-1`}
              aria-label="Sistema"
            >
              {visibleSettings.map((item) => navigationLink(item, compact, onNavigate))}
            </nav>
          </>
        )}
      </div>
      <div className="mt-5 border-t border-brand-100 pt-4">
        <div
          className={`flex items-center rounded-xl ${compact ? 'flex-col gap-2 px-1 py-1.5' : 'gap-3 px-2 py-1.5'}`}
        >
          <span
            className="grid size-9 shrink-0 place-items-center rounded-full bg-brand-100 text-xs font-bold text-brand-700 ring-2 ring-brand-200/40"
            title={compact ? session.user.displayName : undefined}
          >
            {initials(session.user.displayName)}
          </span>
          {!compact && (
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium text-ink-950">
                {session.user.displayName}
              </span>
              <span className="block truncate pt-0.5 text-xs text-slate-500">
                {session.user.role === 'ADMIN' ? 'Administrador' : 'Operador'}
              </span>
            </span>
          )}
          <button
            type="button"
            onClick={onLogout}
            className="rounded-lg p-2 text-slate-500 transition-colors hover:bg-brand-100 hover:text-ink-950"
            title="Cerrar sesión"
            aria-label="Cerrar sesión"
          >
            <LogOut size={16} aria-hidden="true" />
          </button>
        </div>
      </div>
    </aside>
  );
}

export function AppShell({
  session,
  children,
  onLogout,
}: Readonly<{
  session: AuthSession;
  children: React.ReactNode;
  onLogout: () => void;
}>): React.JSX.Element {
  const [mobileNavigationOpen, setMobileNavigationOpen] = useState(false);
  const [sidebarHovered, setSidebarHovered] = useState(false);
  const [sidebarLockMode, setSidebarLockMode] = useState<SidebarLockMode>('auto');
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const headerMenusRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const jobs = useJobs({ pageSize: 100 }, session.user.role === 'ADMIN');

  const desktopSidebarExpanded =
    sidebarLockMode === 'open' || (sidebarLockMode === 'auto' && sidebarHovered);
  const desktopSidebarWidth = desktopSidebarExpanded ? 272 : 80;
  const failedJobs = useMemo(
    () => (jobs.data ?? []).filter((job) => job.status === 'DEAD_LETTER'),
    [jobs.data],
  );
  const runningJobs = useMemo(
    () =>
      (jobs.data ?? []).filter(
        (job) => job.status === 'PENDING' || job.status === 'RUNNING' || job.status === 'RETRYING',
      ),
    [jobs.data],
  );
  const notificationCount =
    (session.organization.state === 'ACTIVE' ? 0 : 1) +
    (session.user.role === 'ADMIN' ? failedJobs.length : 0);

  useEffect(() => {
    function closeMenus(event: MouseEvent): void {
      if (!headerMenusRef.current?.contains(event.target as Node)) {
        setNotificationsOpen(false);
        setAccountOpen(false);
      }
    }

    function closeOnEscape(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        setMobileNavigationOpen(false);
        setNotificationsOpen(false);
        setAccountOpen(false);
      }
    }

    document.addEventListener('mousedown', closeMenus);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeMenus);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, []);

  function toggleSidebarLock(): void {
    if (sidebarLockMode === 'auto') {
      setSidebarLockMode(desktopSidebarExpanded ? 'open' : 'closed');
      return;
    }
    setSidebarLockMode('auto');
  }

  return (
    <div className="min-h-screen text-slate-800">
      <div
        className="fixed inset-y-0 left-0 z-30 hidden transition-[width] duration-200 ease-out md:block"
        style={{ width: desktopSidebarWidth }}
        onMouseEnter={() => setSidebarHovered(true)}
        onMouseLeave={() => setSidebarHovered(false)}
      >
        <Sidebar
          session={session}
          compact={!desktopSidebarExpanded}
          onLogout={onLogout}
          lockMode={sidebarLockMode}
          onToggleLock={toggleSidebarLock}
        />
      </div>
      {mobileNavigationOpen && (
        <div
          className="fixed inset-0 z-50 md:hidden"
          role="dialog"
          aria-modal="true"
          aria-label="Navegación"
        >
          <div aria-hidden="true" className="absolute inset-0 bg-ink-950/35 backdrop-blur-[1px]" />
          <div className="relative h-full w-[272px] animate-in slide-in-from-left-2 duration-200">
            <button
              type="button"
              onClick={() => setMobileNavigationOpen(false)}
              className="absolute top-5 right-3 z-10 rounded-lg bg-brand-50 p-2 text-slate-600 hover:bg-brand-100 hover:text-ink-950"
              aria-label="Cerrar navegación"
            >
              <X size={18} aria-hidden="true" />
            </button>
            <Sidebar
              session={session}
              onNavigate={() => setMobileNavigationOpen(false)}
              onLogout={onLogout}
            />
          </div>
        </div>
      )}
      <div
        className="app-shell-content min-h-screen transition-[padding] duration-200 ease-out"
        style={{ '--desktop-sidebar-width': `${desktopSidebarWidth}px` } as React.CSSProperties}
      >
        <header className="sticky top-0 z-20 flex h-[68px] items-center border-b border-slate-200/80 bg-slate-25/80 px-4 backdrop-blur-xl sm:px-6 lg:px-8">
          <button
            type="button"
            onClick={() => setMobileNavigationOpen(true)}
            className="mr-3 rounded-xl p-2 text-slate-600 hover:bg-slate-100 md:hidden"
            aria-label="Abrir navegación"
          >
            <Menu size={21} aria-hidden="true" />
          </button>
          <div className="min-w-0 flex-1">
            <div className="hidden items-center gap-2 sm:flex">
              <span className="truncate text-sm font-semibold text-slate-800">
                Huilen Kids Online
              </span>
              <span className="text-slate-300">/</span>
              <OrganizationStateBadge state={session.organization.state} />
            </div>
            <div className="flex items-center gap-2 sm:hidden">
              <span className="truncate text-sm font-semibold text-slate-800">
                Huilen Kids Online
              </span>
              <OrganizationStateBadge state={session.organization.state} />
            </div>
          </div>
          <div ref={headerMenusRef} className="relative flex items-center gap-1.5">
            <div className="relative">
              <button
                type="button"
                onClick={() => {
                  setNotificationsOpen((open) => !open);
                  setAccountOpen(false);
                }}
                className={`relative rounded-xl p-2.5 transition-colors ${
                  notificationsOpen
                    ? 'bg-brand-50 text-brand-700'
                    : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
                }`}
                aria-label="Notificaciones"
                aria-expanded={notificationsOpen}
                aria-haspopup="menu"
              >
                <Bell size={19} aria-hidden="true" />
                {notificationCount > 0 && (
                  <span className="absolute top-1.5 right-1.5 grid min-w-4 place-items-center rounded-full bg-rose-500 px-1 text-[9px] font-bold leading-4 text-white ring-2 ring-slate-25">
                    {notificationCount > 9 ? '9+' : notificationCount}
                  </span>
                )}
              </button>
              {notificationsOpen && (
                <div
                  className="absolute top-[calc(100%+10px)] right-0 z-50 w-[min(360px,calc(100vw-2rem))] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-slate-900/10"
                  role="menu"
                >
                  <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3.5">
                    <div>
                      <p className="text-sm font-semibold text-ink-950">Notificaciones</p>
                      <p className="mt-0.5 text-xs text-slate-500">Estado operativo del sistema</p>
                    </div>
                    {notificationCount > 0 && (
                      <span className="rounded-full bg-rose-50 px-2 py-1 text-[10px] font-semibold text-rose-600">
                        {notificationCount} pendiente{notificationCount === 1 ? '' : 's'}
                      </span>
                    )}
                  </div>
                  <div className="max-h-[360px] overflow-y-auto p-2 app-scrollbar">
                    {session.organization.state !== 'ACTIVE' && (
                      <button
                        type="button"
                        onClick={() => {
                          setNotificationsOpen(false);
                          if (session.user.role === 'ADMIN') void navigate('/configuracion');
                        }}
                        className="w-full rounded-xl px-3 py-3 text-left hover:bg-amber-50"
                      >
                        <p className="text-xs font-semibold text-amber-800">
                          {session.organization.state === 'SETUP'
                            ? 'Sincronización todavía no activada'
                            : session.organization.state === 'READ_ONLY'
                              ? 'Sistema en modo solo lectura'
                              : 'Sistema en prueba interna'}
                        </p>
                        <p className="mt-1 text-xs leading-5 text-slate-500">
                          {session.organization.state === 'SETUP'
                            ? 'El stock externo no se modificará hasta completar la configuración.'
                            : session.organization.state === 'READ_ONLY'
                              ? 'Se detectan diferencias, pero no se publica stock externo.'
                              : 'Se actualiza el stock interno y se registran intenciones; el stock externo sigue bloqueado.'}
                        </p>
                      </button>
                    )}
                    {session.user.role === 'ADMIN' && failedJobs.length > 0 && (
                      <button
                        type="button"
                        onClick={() => {
                          setNotificationsOpen(false);
                          void navigate('/configuracion/integraciones/jobs');
                        }}
                        className="w-full rounded-xl px-3 py-3 text-left hover:bg-rose-50"
                      >
                        <p className="text-xs font-semibold text-rose-700">
                          {failedJobs.length} job{failedJobs.length === 1 ? '' : 's'} requiere
                          {failedJobs.length === 1 ? '' : 'n'} atención
                        </p>
                        <p className="mt-1 text-xs leading-5 text-slate-500">
                          Revisá los errores y reintentá las tareas que correspondan.
                        </p>
                      </button>
                    )}
                    {session.user.role === 'ADMIN' && runningJobs.length > 0 && (
                      <button
                        type="button"
                        onClick={() => {
                          setNotificationsOpen(false);
                          void navigate('/configuracion/integraciones/jobs');
                        }}
                        className="w-full rounded-xl px-3 py-3 text-left hover:bg-brand-50"
                      >
                        <p className="text-xs font-semibold text-brand-700">
                          {runningJobs.length} tarea{runningJobs.length === 1 ? '' : 's'} en cola o
                          proceso
                        </p>
                        <p className="mt-1 text-xs leading-5 text-slate-500">
                          Podés seguir su avance desde Jobs y errores.
                        </p>
                      </button>
                    )}
                    {notificationCount === 0 && runningJobs.length === 0 && (
                      <div className="px-3 py-7 text-center">
                        <p className="text-sm font-medium text-slate-700">Todo al día</p>
                        <p className="mt-1 text-xs text-slate-500">
                          No hay alertas que requieran atención.
                        </p>
                      </div>
                    )}
                  </div>
                  {session.user.role === 'ADMIN' && (
                    <button
                      type="button"
                      onClick={() => {
                        setNotificationsOpen(false);
                        void navigate('/configuracion/integraciones/jobs');
                      }}
                      className="w-full border-t border-slate-100 px-4 py-3 text-center text-xs font-semibold text-brand-700 hover:bg-brand-50"
                    >
                      Ver Jobs y errores
                    </button>
                  )}
                </div>
              )}
            </div>

            <div className="relative hidden sm:block">
              <button
                type="button"
                onClick={() => {
                  setAccountOpen((open) => !open);
                  setNotificationsOpen(false);
                }}
                className={`flex items-center gap-1 rounded-xl p-2 transition-colors ${
                  accountOpen
                    ? 'bg-brand-50 text-brand-700'
                    : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
                }`}
                aria-label="Menú de usuario"
                aria-expanded={accountOpen}
                aria-haspopup="menu"
              >
                <span className="grid size-7 place-items-center rounded-full bg-brand-100 text-[10px] font-bold text-brand-700">
                  {initials(session.user.displayName)}
                </span>
                <ChevronDown
                  size={15}
                  aria-hidden="true"
                  className={`transition-transform ${accountOpen ? 'rotate-180' : ''}`}
                />
              </button>
              {accountOpen && (
                <div
                  className="absolute top-[calc(100%+10px)] right-0 z-50 w-64 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-slate-900/10"
                  role="menu"
                >
                  <div className="border-b border-slate-100 px-4 py-3.5">
                    <p className="truncate text-sm font-semibold text-ink-950">
                      {session.user.displayName}
                    </p>
                    <p className="mt-0.5 truncate text-xs text-slate-500">{session.user.email}</p>
                    <span className="mt-2 inline-flex rounded-full bg-brand-50 px-2 py-1 text-[10px] font-semibold text-brand-700">
                      {session.user.role === 'ADMIN' ? 'Administrador' : 'Operador'}
                    </span>
                  </div>
                  {session.user.role === 'ADMIN' && (
                    <button
                      type="button"
                      onClick={() => {
                        setAccountOpen(false);
                        void navigate('/configuracion');
                      }}
                      className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm text-slate-600 hover:bg-slate-50 hover:text-ink-950"
                      role="menuitem"
                    >
                      <Settings2 size={16} aria-hidden="true" />
                      Configuración
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      setAccountOpen(false);
                      onLogout();
                    }}
                    className="flex w-full items-center gap-2 border-t border-slate-100 px-4 py-3 text-left text-sm text-rose-600 hover:bg-rose-50"
                    role="menuitem"
                  >
                    <LogOut size={16} aria-hidden="true" />
                    Cerrar sesión
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>
        {session.organization.state !== 'ACTIVE' && (
          <div className="border-b border-amber-100 bg-amber-50 px-4 py-2.5 sm:px-6 lg:px-8">
            <div className="mx-auto flex max-w-[1520px] items-center gap-2 text-xs text-amber-800">
              <ShieldCheck size={16} aria-hidden="true" />
              <span>
                {session.organization.state === 'SETUP'
                  ? 'Estás en configuración. El stock externo no se modificará hasta activar la sincronización.'
                  : session.organization.state === 'READ_ONLY'
                    ? 'Modo solo lectura: se detectan diferencias, pero no se publica stock externo.'
                    : 'Prueba interna: el stock del sistema se actualiza y se registran las publicaciones previstas, pero no se escribe en las plataformas.'}
              </span>
              {session.user.role === 'ADMIN' && (
                <button
                  type="button"
                  onClick={() => void navigate('/configuracion')}
                  className="ml-auto hidden font-semibold underline underline-offset-2 sm:inline"
                >
                  Ver diagnóstico
                </button>
              )}
            </div>
          </div>
        )}
        <main className="mx-auto max-w-[1520px] px-4 py-6 sm:px-6 sm:py-8 lg:px-8">{children}</main>
      </div>
    </div>
  );
}
